"use client";

import { memo, useMemo } from "react";
import Image from "next/image";
import Link from "next/link";
import { Telescope, LineChart, ArrowLeftRight, PieChart, BarChart3, Search, Coins, History, Zap, Crown } from "lucide-react";
import type { FeedSession } from "@/lib/api-client";
import { cn } from "@/lib/utils";
import { buildCommunityChatTourUrl } from "@/lib/onboarding-constants";

interface FeedCardProps {
  session: FeedSession;
  compact?: boolean;
  /** When true, render the bordered full-height card shell that matches
   *  the Automations + Blogs cards on the home showcase (image on top,
   *  meta INSIDE a footer that fills the fixed card height). Used by the
   *  Community Use Cases tab so all three tabs share one card type+size. */
  framed?: boolean;
  /** When true the card's href is rewritten to the tour URL
   *  (`/chat/{id}?onboarding_chat_share=1`) so the community
   *  shared-chat tour picks up the next leg of the onboarding chain. */
  tourActive?: boolean;
  /** When true the card receives `data-tour="community-first-card"`
   *  so the CommunityBuddyTour click_target beat can anchor on it. */
  tourTarget?: boolean;
}

// Map backend avatar paths to frontend paths
function mapAvatarPath(backendPath: string | null | undefined): string {
  if (!backendPath) return '/images/avatars/avatar_1.svg';
  return backendPath.replace('/static/avatars/', '/images/avatars/');
}

function formatTimeAgo(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) return "1m";
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h`;
  if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d`;
  return `${Math.floor(diffInSeconds / 604800)}w`;
}


function getCategoryIcon(category: string | null) {
  switch (category) {
    case "Stock Analysis": return LineChart;
    case "Options Trading": return ArrowLeftRight;
    case "Portfolio Strategy": return PieChart;
    case "Technical Analysis": return BarChart3;
    case "Market Research": return Search;
    case "Crypto Analysis": return Coins;
    case "Backtesting": return History;
    default: return Telescope;
  }
}

function PureFeedCard({
  session,
  compact = false,
  framed = false,
  tourActive = false,
  tourTarget = false,
}: FeedCardProps) {
  const CategoryIcon = getCategoryIcon(session.category);

  // ── Framed variant: bordered, full-height card shell that matches the
  //    Automations + Blogs cards exactly (same border/bg/radius, image on
  //    top, footer fills the rest). Lets the Community tab share one card
  //    type + size with the other two home tabs.
  if (framed) {
    return (
      <Link
        href={
          tourActive
            ? buildCommunityChatTourUrl(session.session_id)
            : `/chat/${session.session_id}`
        }
        data-tour={tourTarget ? "community-first-card" : undefined}
        prefetch={false}
        className={cn(
          "group relative flex h-full w-full cursor-pointer flex-col overflow-hidden rounded-2xl",
          "border border-white/[0.06] bg-[#0c0c0e] shadow-[0_2px_12px_rgba(0,0,0,0.5)]",
          "transition-[transform,border-color,box-shadow] duration-300 ease-[cubic-bezier(0.23,1,0.32,1)]",
          "hover:border-emerald-500/20 hover:shadow-[0_8px_32px_rgba(16,185,129,0.06)] hover:-translate-y-0.5",
        )}
      >
        {/* Thumbnail */}
        <div className="relative aspect-[16/9] w-full shrink-0 overflow-hidden bg-zinc-900">
          {session.last_image ? (
            <Image
              src={session.last_image}
              alt={session.title}
              fill
              className="object-cover transition-transform duration-500 group-hover:scale-[1.03]"
              unoptimized
            />
          ) : (
            <div className="absolute inset-0 flex flex-col justify-center p-5">
              <p className="line-clamp-4 text-[13px] font-normal leading-relaxed text-zinc-300">
                {session.preview_answer}
              </p>
            </div>
          )}
          <div className="absolute right-3 top-3">
            <div className="flex items-center gap-1.5 rounded-md border border-white/10 bg-black/60 px-2 py-1 backdrop-blur-sm">
              <CategoryIcon className="h-3 w-3 text-white/80" />
              <span className="text-[10px] font-medium uppercase tracking-wider text-white/90">
                {session.category?.split(" ")[0] || "Rsrch"}
              </span>
            </div>
          </div>
        </div>

        {/* Footer: one-line title + one meta row, same layout as the other
         *  home-tab cards. */}
        <div className="flex flex-1 flex-col gap-2.5 px-4 pt-3.5 pb-4">
          <h3 className="line-clamp-1 text-[14.5px] font-semibold leading-snug text-slate-50 transition-colors group-hover:text-white">
            {session.preview_question || session.title || "New Analysis Session"}
          </h3>
          <div className="mt-auto flex items-center gap-2 text-[12px]">
            <div className="relative h-6 w-6 shrink-0 overflow-hidden rounded-full border border-zinc-700 bg-zinc-900">
              <Image
                src={mapAvatarPath(session.user_avatar)}
                alt={session.username}
                fill
                className="object-cover"
                unoptimized
              />
            </div>
            <span className="truncate font-medium text-slate-200">
              {session.username}
            </span>
            {session.user_plan !== "free" && (
              <span
                className={cn(
                  "flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide",
                  session.user_plan === "pro"
                    ? "bg-amber-400/10 text-amber-400"
                    : "bg-blue-400/10 text-blue-400",
                )}
              >
                {session.user_plan === "pro" && <Crown className="h-2 w-2" />}
                {session.user_plan === "plus" && <Zap className="h-2 w-2" />}
                {session.user_plan}
              </span>
            )}
            <span className="ml-auto shrink-0 text-zinc-400">
              {formatTimeAgo(session.updated_at)}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      href={
        tourActive
          ? buildCommunityChatTourUrl(session.session_id)
          : `/chat/${session.session_id}`
      }
      data-tour={tourTarget ? "community-first-card" : undefined}
      className={cn(
        "group flex w-full cursor-pointer flex-col mx-auto md:mx-0",
        compact
          ? "max-w-[17.75rem] gap-2.5 md:max-w-[18.5rem]"
          : "gap-3"
      )}
      prefetch={false}
    >
      {/* 1. Image Container - The "Card" part */}
      <div className={cn(
        "relative w-full overflow-hidden border border-zinc-800 bg-zinc-900 transition-all duration-300 group-hover:border-zinc-600 group-hover:shadow-md",
        compact ? "aspect-[16/9] rounded-[18px]" : "aspect-[16/9] rounded-xl md:aspect-[16/9]"
      )}>
        {session.last_image ? (
          <Image
            src={session.last_image}
            alt={session.title}
            fill
            className="object-cover transition-transform duration-500 group-hover:scale-105"
            unoptimized
          />
        ) : (
          <div className="absolute inset-0 p-6 flex flex-col justify-center bg-transparent">
            <p className={cn(
              "font-normal leading-relaxed text-zinc-300 transition-colors group-hover:text-zinc-200",
              compact ? "line-clamp-4 text-[13px]" : "line-clamp-5 text-lg"
            )}>
              {session.preview_answer}
            </p>
          </div>
        )}
        
        {/* Optional: Category Badge overlaid on image (bottom right) */}
        <div className="absolute bottom-3 right-3">
          <div className={cn(
            "flex items-center rounded-md border border-white/10 bg-black/60 backdrop-blur-sm",
            compact ? "gap-1 px-1.5 py-0.5" : "gap-1.5 px-2 py-1"
          )}>
            <CategoryIcon className={cn("text-white/80", compact ? "h-2.5 w-2.5" : "h-3 w-3")} />
            <span className={cn(
              "font-medium uppercase tracking-wider text-white/90",
              compact ? "text-[9px]" : "text-[10px]"
            )}>
              {session.category?.split(" ")[0] || "Rsrch"}
            </span>
          </div>
        </div>
      </div>
      
      {/* 2. Meta Info - Floating below */}
      <div className={cn("flex px-1", compact ? "gap-2.5" : "gap-3")}>
        {/* Left: Avatar */}
        <div className="shrink-0 pt-1">
          <div className={cn(
            "relative overflow-hidden rounded-full border border-zinc-800 bg-zinc-900",
            compact ? "h-7 w-7" : "h-8 w-8"
          )}>
            <Image 
              src={mapAvatarPath(session.user_avatar)} 
              alt={session.username}
              fill
              className="object-cover"
              unoptimized
            />
          </div>
        </div>

        {/* Right: Title & Details */}
        <div className={cn("flex min-w-0 flex-col", compact ? "gap-1" : "gap-1.5")}>
          {/* Title */}
          <h3 className={cn(
            "font-medium leading-snug text-zinc-200 transition-colors group-hover:text-white",
            compact ? "line-clamp-2 text-[13px]" : "line-clamp-2 text-base md:line-clamp-1 md:text-sm"
          )}>
            {session.preview_question || session.title || "New Analysis Session"}
          </h3>
          
          {/* Sub-row: Name • Plan • Messages • Time */}
          <div className={cn("flex items-center text-white", compact ? "gap-1.5 text-[10px]" : "gap-2 text-xs")}>
            <span className={cn(
              "truncate font-medium text-white",
              compact ? "max-w-[78px]" : "max-w-[100px] md:max-w-[80px]"
            )}>{session.username}</span>
            
            {session.user_plan !== 'free' && (
              <span className={cn(
                "rounded font-bold uppercase tracking-wide flex items-center gap-0.5",
                compact ? "px-1 py-0.5 text-[9px]" : "px-1.5 py-0.5 text-[10px]",
                session.user_plan === 'pro' ? "text-amber-400 bg-amber-400/10" : "text-blue-400 bg-blue-400/10"
              )}>
                {session.user_plan === 'pro' && <Crown className={cn(compact ? "h-2 w-2" : "h-2 w-2")} />}
                {session.user_plan === 'plus' && <Zap className={cn(compact ? "h-2 w-2" : "h-2 w-2")} />}
                {session.user_plan}
              </span>
            )}

            <span>{formatTimeAgo(session.updated_at)}</span>
          </div>
        </div>
      </div>
    </Link>
  );
}

export const FeedCard = memo(PureFeedCard);

export function FeedCardSkeleton({ compact = false, framed = false }: { compact?: boolean; framed?: boolean }) {
  if (framed) {
    return (
      <div className="flex h-full w-full flex-col overflow-hidden rounded-2xl border border-white/[0.06] bg-[#0c0c0e]">
        <div className="aspect-[16/9] w-full shrink-0 bg-zinc-900/60" />
        <div className="flex flex-1 flex-col gap-2.5 px-4 pt-3.5 pb-4">
          <div className="h-4 w-3/4 rounded bg-zinc-800/70" />
          <div className="mt-auto flex items-center gap-2">
            <div className="h-6 w-6 shrink-0 rounded-full bg-zinc-800/70" />
            <div className="h-3.5 w-24 rounded bg-zinc-800/70" />
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className={cn("flex flex-col", compact ? "gap-2.5" : "gap-3")}>
      {/* Image Skeleton */}
      <div className={cn(
        "w-full rounded-xl border border-zinc-800/50 bg-zinc-900/50",
        compact ? "aspect-[16/9] rounded-[18px]" : "aspect-[16/7] md:aspect-[16/10]"
      )} />
      
      {/* Meta Skeleton */}
      <div className={cn("flex px-1", compact ? "gap-2.5" : "gap-3")}>
        <div className={cn("shrink-0 rounded-full bg-zinc-900/50", compact ? "h-7 w-7" : "h-8 w-8")} />
        <div className="flex flex-col gap-2 w-full pt-1">
          <div className={cn("rounded bg-zinc-900/50", compact ? "h-3.5 w-2/3" : "h-4 w-3/4")} />
          <div className={cn("rounded bg-zinc-900/50", compact ? "h-2.5 w-1/2" : "h-3 w-1/2")} />
        </div>
      </div>
    </div>
  );
}
